import { createSvgMap } from "./svg/map";
import { attachSvgInteraction } from "./svg/interaction";
import { createFilters } from "./ui/filters";
import type { FilterHandle } from "./ui/filters";
import { createSearch } from "./ui/search";
import { initFullscreen } from "./ui/fullscreen";
import { closeSidebar, initSidebar, openLocationSidebar } from "./ui/sidebar";
import { fetchMapData } from "./map/api";
import { normalizeFloorOrders } from "./map/filters";
import type { Category, CategoryStyle } from "./map/model";

const container = document.getElementById("map")!;

(async () => {
  // API verisi tek noktada tutarlı hâle getirilir: aynı kat adına farklı
  // floorOrder verilmiş kayıtlar sıralamayı ve parsel rengini bozuyordu.
  const data = normalizeFloorOrders(await fetchMapData());
  const stylesByCategory = new Map<Category, CategoryStyle>(data.categoryStyles.map((s) => [s.category, s]));
  const map = await createSvgMap(container, data);
  initSidebar();

  const openLocation = (id: string, level?: string, category?: string) => {
    const loc = map.getLocation(id);
    if (!loc || !loc.units.length) { closeSidebar(); return; }
    openLocationSidebar(loc, stylesByCategory, level, category as Category | undefined);
  };

  const topbar = document.createElement("div");
  topbar.id = "topbar";
  document.body.appendChild(topbar);

  const ctrl = attachSvgInteraction(map.svg, {
    onParcelClick(locationId, level, category) {
      openLocation(locationId, level, category);
    },
    onEmptyClick() {
      closeSidebar();
    },
  }, { bounds: map.bounds });

  // `filters` aşağıda kuruluyor (arama kutusu topbar'da ÜSTTE kalsın diye), ama
  // onSelect ancak kullanıcı bir sonuç seçince çalışır — o an atanmış olur.
  let filters: FilterHandle | undefined;

  createSearch(topbar, data, stylesByCategory, {
    onSelect(locationId, level, category) {
      // Aranan kiracı filtrelenmişse önce onu görünür kıl, sonra odaklan.
      filters?.reveal(category, level);
      const loc = map.getLocation(locationId);
      if (loc?.svgParcelId) {
        ctrl.focusParcel(loc.svgParcelId);
        pulseParcel(map.svg, loc.svgParcelId);
      }
      openLocation(locationId, level, category);
    },
  });
  // Kategori + kat filtresi: tek durum, tek apply() (renk ve rozet dahil)
  filters = createFilters(data, topbar, map.filterTargets, stylesByCategory);

  // Zoom + fullscreen kontrolleri
  const controls = document.createElement("div");
  controls.id = "map-controls";
  const mkBtn = (label: string, title: string, fn: () => void) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.title = title;
    b.addEventListener("click", fn);
    return b;
  };
  controls.append(
    mkBtn("+", "Yakınlaştır", () => ctrl.zoomIn()),
    mkBtn("−", "Uzaklaştır", () => ctrl.zoomOut()),
    mkBtn("⌂", "Başlangıç görünümü", () => ctrl.reset()),
  );
  container.appendChild(controls);
  initFullscreen(controls);

  (window as any).svgMap = map;
  console.log(`[map] ${map.parcelCount} parsel, ${data.locations.length} lokasyon yüklendi`);
})().catch((err) => {
  console.error("[main] Harita yüklenemedi:", err);
  const div = document.createElement("div");
  div.className = "fatal";
  div.textContent = "Harita yüklenemedi: " + (err?.message ?? err);
  document.body.appendChild(div);
});

/** Aramadan seçilen parseli kısa süre vurgula. */
let pulseTimer: ReturnType<typeof setTimeout> | null = null;
let pulseEl: SVGPathElement | null = null;
function pulseParcel(svg: SVGSVGElement, id: string) {
  const el = svg.querySelector<SVGPathElement>(`.parcel[data-id="${CSS.escape(id)}"]`);
  if (!el) return;
  if (pulseTimer !== null) {
    clearTimeout(pulseTimer);
    pulseEl?.classList.remove("pulse"); // önceki parselin vurgusunu hemen kaldır
  }
  el.classList.remove("pulse");
  void el.getBBox(); // reflow → animasyon yeniden tetiklensin
  el.classList.add("pulse");
  pulseEl = el;
  pulseTimer = setTimeout(() => {
    el.classList.remove("pulse");
    pulseTimer = null;
    pulseEl = null;
  }, 1300);
}
